import React, { useState } from 'react';
import { Box, Button, Text, Spinner, HStack, Stack, Table, Input } from '@chakra-ui/react';
import SimpleModal from './SimpleModal';
import { api, ZatcaTaxSummaryResponse } from '../api/api';

interface ZatcaTaxSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    machineKey: string;
    systemName: string;
}

function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function formatCurrency(value: number): string {
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const ZatcaTaxSummaryModal: React.FC<ZatcaTaxSummaryModalProps> = ({ isOpen, onClose, machineKey, systemName }) => {
    const [from, setFrom] = useState(firstOfMonthIso());
    const [to, setTo] = useState(todayIso());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [summary, setSummary] = useState<ZatcaTaxSummaryResponse | null>(null);

    const handleCalculate = async () => {
        if (!machineKey) return;
        setLoading(true);
        setError('');
        try {
            const data = await api.zatcaXml.taxSummary(machineKey, from, to);
            setSummary(data);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to calculate tax summary.');
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSummary(null);
        setError('');
        onClose();
    };

    return (
        <SimpleModal isOpen={isOpen} onClose={handleClose} title={`Tax Summary — ${systemName}`} size="lg">
            <Stack gap={4}>
                <HStack gap={3} align="end" flexWrap="wrap">
                    <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>From</Text>
                        <Input size="sm" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>To</Text>
                        <Input size="sm" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                    </Box>
                    <Button size="sm" colorPalette="blue" onClick={handleCalculate} loading={loading}>
                        Calculate
                    </Button>
                </HStack>

                {loading ? (
                    <Box textAlign="center" py={8}><Spinner size="lg" /></Box>
                ) : error ? (
                    <Text color="red.500">{error}</Text>
                ) : summary ? (
                    <Stack gap={4}>
                        <Table.Root size="sm" variant="outline">
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader></Table.ColumnHeader>
                                    <Table.ColumnHeader>Count</Table.ColumnHeader>
                                    <Table.ColumnHeader>Total Amount (SAR)</Table.ColumnHeader>
                                    <Table.ColumnHeader>Taxable Amount (SAR)</Table.ColumnHeader>
                                    <Table.ColumnHeader>Tax (SAR)</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                <Table.Row>
                                    <Table.Cell fontWeight="medium">Sales</Table.Cell>
                                    <Table.Cell>{summary.salesCount}</Table.Cell>
                                    <Table.Cell>{formatCurrency(summary.salesAmountTotal)}</Table.Cell>
                                    <Table.Cell>{formatCurrency(summary.salesTaxableTotal)}</Table.Cell>
                                    <Table.Cell>{formatCurrency(summary.salesTaxTotal)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell fontWeight="medium">Returns</Table.Cell>
                                    <Table.Cell>{summary.returnsCount}</Table.Cell>
                                    <Table.Cell>{formatCurrency(summary.returnsAmountTotal)}</Table.Cell>
                                    <Table.Cell>{formatCurrency(summary.returnsTaxableTotal)}</Table.Cell>
                                    <Table.Cell>{formatCurrency(summary.returnsTaxTotal)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell fontWeight="bold">Net Payable</Table.Cell>
                                    <Table.Cell></Table.Cell>
                                    <Table.Cell fontWeight="bold">{formatCurrency(summary.netAmountTotal)}</Table.Cell>
                                    <Table.Cell fontWeight="bold">{formatCurrency(summary.netTaxableTotal)}</Table.Cell>
                                    <Table.Cell fontWeight="bold">{formatCurrency(summary.netTaxAmount)}</Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        </Table.Root>

                        {summary.parseErrorCount > 0 && (
                            <Text fontSize="sm" color="orange.500">
                                {summary.parseErrorCount} record{summary.parseErrorCount !== 1 ? 's' : ''} could not be parsed and were excluded from this summary.
                            </Text>
                        )}

                        <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={2}>Category Breakdown (net of returns)</Text>
                            {summary.categoryBreakdown.length === 0 ? (
                                <Text color="gray.500" fontSize="sm">No category data found for this range.</Text>
                            ) : (
                                <Table.Root size="sm" variant="outline">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColumnHeader>Category</Table.ColumnHeader>
                                            <Table.ColumnHeader>Percent</Table.ColumnHeader>
                                            <Table.ColumnHeader>Taxable Amount (SAR)</Table.ColumnHeader>
                                            <Table.ColumnHeader>Tax Amount (SAR)</Table.ColumnHeader>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {summary.categoryBreakdown.map(cat => (
                                            <Table.Row key={cat.categoryId}>
                                                <Table.Cell>{cat.categoryId}</Table.Cell>
                                                <Table.Cell>{cat.percent}%</Table.Cell>
                                                <Table.Cell>{formatCurrency(cat.taxableAmount)}</Table.Cell>
                                                <Table.Cell>{formatCurrency(cat.taxAmount)}</Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table.Root>
                            )}
                        </Box>
                    </Stack>
                ) : (
                    <Text color="gray.500" textAlign="center" py={8}>Choose a date range and click Calculate.</Text>
                )}
            </Stack>
        </SimpleModal>
    );
};

export default ZatcaTaxSummaryModal;
